<template>
  <main>
    <div class="row">
      <!-- Tabs status -->
      <div class="col-12">
        <b-tabs v-model="statusTabIndex" @input="onStatusTab">
          <b-tab :title="$t('all')"></b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #28a745"></span> {{ $t('success') }}
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #dc3545"></span> {{ $t('error') }}
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline mb-2">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                          :placeholder="$t('keyword')" style="min-width: 220px"></b-form-input>
            <client-select class="mr-1 mb-1" :model="filterFormModel" attribute="client" @change="search"
                           :isFilter="true" :place-holder="$t('selectClient')"></client-select>
            <time-range-picker :model="filterFormModel" :label="$t('time')" class="mr-1 mb-1"
                               :defaultShortcut="3"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
            <webhook-vietguys-export class="ml-auto mb-1" :filter="filterFormModel" :total-count="itemCount"/>
          </div>
          <div class="form-inline">
            <small class="text-muted mr-2 mb-1">Lọc nâng cao:</small>
            <sms-brandname-select :model="filterFormModel" attribute="brandName" client-attribute="client" @input="load"
                                  :isFilter="true" class="mr-1 mb-1"/>
            <sms-template-select :model="filterFormModel" attribute="template" client-attribute="client" @input="load"
                                 :isFilter="true" class="mr-1 mb-1"/>
            <form-select :model="filterFormModel" attribute="type" class="mr-1 mb-1" :options="[
              {text: $t('type'), value: null},
              {text: $t('ads'), value: 'ads'},
              {text: $t('care'), value: 'care'},
            ]"></form-select>
            <form-select :model="filterFormModel" attribute="timeFilter" class="mr-1 mb-1" :options="[
              {text: $t('filterByCreateTime'), value: 'createTime'},
              {text: $t('filterBySendTime'), value: 'sendTime'},
            ]"></form-select>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card" :class="{ 'log-card--error': item.status === 'fail' }"
               :style="{ borderLeftColor: item.type === 'ads' ? '#039be5' : '#7e57c2' }"
               @click="showDetailModal(item)" role="button">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm:ss') }}</div>
              <div class="log-meta" v-if="item.sendTime">Gửi: {{ item.sendTime | moment('HH:mm') }}</div>
              <div class="log-meta" v-if="item.total">{{ item.total }} tin</div>
            </div>

            <div class="log-card__type">
              <span class="log-type-pill"
                    :style="{ background: item.type === 'ads' ? '#039be5' : '#7e57c2' }">
                {{ $t(item.type) }}
              </span>
              <div class="log-meta mt-1" v-if="item.brandName">{{ item.brandName.name }}</div>
              <div class="log-meta" v-if="item.template">{{ item.template.name }}</div>
            </div>

            <div class="log-card__main">
              <div class="log-row-1">
                <font-awesome-icon icon="mobile-alt" class="text-muted mr-1"/>
                <strong>{{ item.number || '—' }}</strong>
                <span class="log-meta ml-2" v-if="item.telco">[{{ item.telco }}]</span>
                <span class="log-meta ml-2" v-if="item.messageCampaignContact && item.messageCampaignContact.name">
                  {{ item.messageCampaignContact.name }}
                </span>
              </div>
              <div class="log-row-2 log-content" v-if="item.sms && item.sms.content">{{ item.sms.content }}</div>
              <div class="log-row-3" v-if="item.client">
                <font-awesome-icon icon="building" class="text-muted mr-1"/>
                <span class="log-meta">{{ item.client.domain }}</span>
                <span v-if="item.messageCampaign" class="log-meta ml-2">
                  <font-awesome-icon icon="rocket" class="mr-1"/>{{ item.messageCampaign.name }}
                </span>
              </div>
            </div>

            <div class="log-card__status">
              <b-badge v-if="item.status === 'fail'" variant="danger">{{ $t('error') }}</b-badge>
              <b-badge v-else variant="success">{{ $t('success') }}</b-badge>
            </div>

            <div class="log-card__chev"><font-awesome-icon icon="chevron-right"/></div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-4" v-if="!items.length">
        <font-awesome-icon icon="inbox" size="3x" class="mb-2 d-block"/>
        {{ $t('notFoundSearch') }}
      </div>

      <div class="col-12" v-if="itemCount > filterFormModel.pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <b-modal v-model="showDetail" size="xl" hide-footer scrollable :modal-class="['log-modal']"
             :title="$t('webhookDetail')">
      <div v-if="currentItem">
        <div class="log-meta-grid">
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('status') }}</div>
            <b-badge v-if="currentItem.status === 'fail'" variant="danger">{{ $t('error') }}</b-badge>
            <b-badge v-else variant="success">{{ $t('success') }}</b-badge>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('type') }}</div>
            <div>{{ $t(currentItem.type) }}</div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('number') }}</div>
            <div>{{ currentItem.number || '—' }}</div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('network') }}</div>
            <div>{{ currentItem.telco || '—' }}</div>
          </div>
        </div>

        <div class="log-section" v-if="currentItem.sms && currentItem.sms.content">
          <div class="log-section__label log-section__label--big">{{ $t('content') }}</div>
          <div class="log-content-block">{{ currentItem.sms.content }}</div>
        </div>

        <div class="log-section">
          <div class="log-section__label log-section__label--big">Request body</div>
          <div v-if="body">
            <json-viewer :value="parseSafe(body)" :expand-depth="20" :boxed="false" sort
                         theme="dark-json-viewer"></json-viewer>
          </div>
          <div v-else class="text-muted">{{ $t('noData') }}</div>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
import list from '../../mixins/List';
import ClientSelect from "../../components/ClientSelect.vue";
import TimeRangePicker from "../../components/TimeRangePicker.vue";
import SmsBrandnameSelect from "@/components/SmsBrandnameSelect.vue";
import SmsTemplateSelect from "@/components/SmsTemplateSelect.vue";
import _ from 'lodash';
import JsonViewer from 'vue-json-viewer';
import WebhookVietguysExport from "@/views/webhook-vietguys/WebhookVietguysExport.vue";

export default {
  components: { TimeRangePicker, ClientSelect, SmsBrandnameSelect, SmsTemplateSelect, JsonViewer, WebhookVietguysExport },
  mixins: [list],
  data() {
    return {
      service: '/vietguys-log',
      autoLoad: true,
      statusTabIndex: 0,
      defaultFilterFormModel: {
        keyword: null, client: null, brandName: null, template: null,
        type: null, status: null, timeFilter: 'createTime'
      },
      showDetail: false,
      currentItem: null,
      body: null
    }
  },
  methods: {
    search: _.debounce(function () {
      this.filterFormModel.page = 1;
      this.load();
    }, 200),
    onStatusTab(idx) {
      this.filterFormModel.status = idx === 0 ? null : (idx === 1 ? 'success' : 'fail');
      this.filterFormModel.page = 1;
      this.load();
    },
    showDetailModal(item) {
      this.currentItem = item;
      this.body = item.body ? item.body : null;
      this.showDetail = true;
    },
    parseSafe(v) {
      if (!v) return {};
      try { return typeof v === 'string' ? JSON.parse(v) : v; } catch (e) { return { raw: v }; }
    }
  },
  i18n: {
    messages: {
      en: {
        all: 'All',
        from: 'Sent from', to: 'Sent to', content: 'Content', time: 'Time',
        createTime: 'Create time', sendTime: 'Send time', care: 'Care', ads: 'Ads', type: 'Type',
        number: 'Number', network: 'Network', campaign: 'Campaign', messageTemplate: 'Template',
        filterByCreateTime: 'Filter by create time', filterBySendTime: 'Filter by send time',
        webhookDetail: 'Webhook detail', noData: 'No data', numberOfMessages: 'Total messages',
      },
      vi: {
        all: 'Tất cả',
        from: 'Gửi từ', to: 'Gửi tới', content: 'Nội dung', time: 'Thời gian',
        createTime: 'Tạo lúc', sendTime: 'Gửi lúc', care: 'CSKH', ads: 'Quảng cáo', type: 'Loại',
        number: 'Số điện thoại', network: 'Nhà mạng', campaign: 'Chiến dịch', messageTemplate: 'Mẫu',
        filterByCreateTime: 'Lọc theo tạo lúc', filterBySendTime: 'Lọc theo gửi lúc',
        webhookDetail: 'Chi tiết webhook', noData: 'Không có dữ liệu', numberOfMessages: 'Tổng tin',
      }
    }
  }
}
</script>

<style scoped>
.log-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.log-list { display: flex; flex-direction: column; gap: 8px; }
.log-card {
  display: flex; align-items: flex-start; background: #fff;
  border: 1px solid #e8eaed; border-left: 4px solid #888; border-radius: 6px;
  padding: 12px 16px; cursor: pointer; transition: all 0.15s; gap: 16px;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0,0,0,0.06); transform: translateY(-1px); }
.log-card--error { background: #fef6f6; }
.log-card__time { min-width: 110px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }
.log-card__type { min-width: 140px; flex-shrink: 0; }
.log-type-pill {
  display: inline-block; padding: 3px 10px; border-radius: 12px;
  color: #fff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px; white-space: nowrap;
}
.log-card__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.log-row-1 { font-size: 14px; color: #222; }
.log-row-2 { font-size: 13px; color: #444; word-break: break-word; }
.log-content { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.log-row-3 { font-size: 12px; }
.log-card__status { display: flex; align-items: flex-start; padding-top: 2px; flex-shrink: 0; }
.log-card__chev { display: flex; align-items: center; color: #bbb; flex-shrink: 0; }

.log-meta-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
  padding: 12px; background: #f8f9fa; border-radius: 6px;
}
.log-meta-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.log-section { margin-top: 20px; }
.log-section__label--big {
  font-size: 14px; color: #222; font-weight: 600;
  padding-bottom: 6px; border-bottom: 1px solid #e8eaed; margin-bottom: 8px;
}
.log-content-block { background: #f6f8fa; padding: 12px; border-radius: 4px; font-size: 14px; line-height: 1.6; }
</style>
<style>.log-modal .modal-xl { max-width: 1100px; } .log-modal .dark-json-viewer { max-height: 600px; overflow: auto; padding: 8px; font-size: 13px; background: #f8f8fb; }</style>
